import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { isAxiosError } from 'axios'
import { Clock, Play, AlertTriangle, RefreshCw, Info, Unlock } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Switch,
  Tooltip,
  useToast,
} from '@/components/ui'
import type { BadgeProps } from '@/components/ui/Badge.tsx'

interface JobStatus {
  key: string
  label: string
  description: string | null
  tags: string[]
  enabled: boolean
  intervalMinutes: number
  lastRunAt: string | null
  lastRunStatus: 'success' | 'error' | null
  lastRunDurationMs: number | null
  lastRunMessage: string | null
  lastRunError: string | null
  runningSince: string | null
  nextDueAt: string | null
  registered: boolean
}

const TAG_VARIANTS: Record<string, BadgeProps['variant']> = {
  SAP: 'warning',      // orange-ish amber — eindeutig SAP
  TANSS: 'success',    // green — TANSS
  Teams: 'brand',      // brand-lila — MS Teams
  Outlook: 'brand',    // brand-lila — Microsoft-Familie
  KI: 'danger',        // rot — KI/Claude (Aufmerksamkeit: Kosten)
  EGIS: 'neutral',     // grau — EGIS Preisdaten
  OpenRegister: 'info', // blue — public registers
  Confluence: 'accent', // accent — Atlassian
  GoCardless: 'success', // green — Banking/GoCardless
  LinkedIn: 'info',    // blau — LinkedIn
}

function tagVariant(tag: string): BadgeProps['variant'] {
  return TAG_VARIANTS[tag] ?? 'neutral'
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'noch nie'
  const diffMs = Date.now() - new Date(iso).getTime()
  const abs = Math.abs(diffMs)
  const minutes = Math.round(abs / 60_000)
  if (minutes < 1) return 'gerade eben'
  if (minutes < 60) return `vor ${minutes} Min`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `vor ${hours} Std`
  const days = Math.round(hours / 24)
  return `vor ${days} Tagen`
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

export function ScheduledJobsTab() {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [intervalEdits, setIntervalEdits] = useState<Record<string, number>>({})

  const { data, isLoading, error, refetch } = useQuery<{ jobs: JobStatus[] }>({
    queryKey: ['cron-jobs'],
    queryFn: () => apiClient.get('/cron/jobs').then((r) => r.data),
    refetchInterval: 15_000,
  })

  const toggleMut = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      apiClient.put(`/cron/jobs/${key}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cron-jobs'] }),
    onError: (e: unknown) => toast({ title: 'Fehler', description: errMsg(e), tone: 'danger' }),
  })

  const intervalMut = useMutation({
    mutationFn: ({ key, intervalMinutes }: { key: string; intervalMinutes: number }) =>
      apiClient.put(`/cron/jobs/${key}`, { intervalMinutes }),
    onSuccess: (_r, vars) => {
      setIntervalEdits((prev) => {
        const next = { ...prev }
        delete next[vars.key]
        return next
      })
      qc.invalidateQueries({ queryKey: ['cron-jobs'] })
      toast({ title: 'Intervall gespeichert', tone: 'success' })
    },
    onError: (e: unknown) => toast({ title: 'Fehler', description: errMsg(e), tone: 'danger' }),
  })

  const runMut = useMutation({
    mutationFn: (key: string) => apiClient.post(`/cron/jobs/${key}/run-now`, {}),
    onSuccess: (_r, key) => {
      qc.invalidateQueries({ queryKey: ['cron-jobs'] })
      toast({ title: 'Job ausgeführt', description: `„${key}" wurde manuell gestartet.`, tone: 'success' })
    },
    onError: (e: unknown) => toast({ title: 'Fehler beim Start', description: errMsg(e), tone: 'danger' }),
  })

  const [pendingReleaseKey, setPendingReleaseKey] = useState<string | null>(null)
  const releaseMut = useMutation({
    mutationFn: (key: string) => apiClient.post(`/cron/jobs/${key}/release-lock`, {}),
    onSuccess: (_r, key) => {
      qc.invalidateQueries({ queryKey: ['cron-jobs'] })
      setPendingReleaseKey(null)
      toast({
        title: 'Lock freigegeben',
        description: `„${key}" kann wieder gestartet werden.`,
        tone: 'success',
      })
    },
    onError: (e: unknown) => {
      setPendingReleaseKey(null)
      toast({ title: 'Lock konnte nicht freigegeben werden', description: errMsg(e), tone: 'danger' })
    },
  })

  const jobs = data?.jobs ?? []
  const [expandedStatus, setExpandedStatus] = useState<Record<string, boolean>>({})

  return (
    <div className="space-y-4">
      <Card className="border-blue-100 bg-blue-50">
        <Card.Body className="flex items-start gap-3 text-body">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-semibold text-gray-900">Zentrale Hintergrund-Jobs</p>
            <p className="text-caption text-gray-600">
              Alle Jobs werden über einen einzigen Plesk-Cron lokal auf dem Server
              ausgelöst — kein öffentlich erreichbarer Endpoint, kein Shared-Secret.
              Der Dispatcher prüft für jeden Job Intervall und Lock und startet nur fällige.
            </p>
            <p className="text-caption text-gray-600">
              Plesk-Cron-Eintrag (Cron-style <code className="font-mono">*/5 * * * *</code>):
              <br />
              <code className="font-mono text-caption bg-white border border-blue-200 rounded px-1 py-0.5 mt-1 inline-block">
                cd /var/www/vhosts/sysman.implec.de/httpdocs/apps/backend && node dist/scripts/cron-tick.js
              </code>
            </p>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title className="flex items-center gap-2 text-body">
            <Clock className="w-4 h-4 text-brand-500" />
            Geplante Aufgaben ({jobs.length})
          </Card.Title>
          <Button
            variant="ghost"
            size="xs"
            leadingIcon={<RefreshCw className="w-3.5 h-3.5" />}
            onClick={() => refetch()}
          >
            Aktualisieren
          </Button>
        </Card.Header>

        {isLoading && <LoadingState label="Lade Jobs …" />}
        {error && <ErrorState title="Jobs konnten nicht geladen werden" description={errMsg(error)} />}

        {!isLoading && jobs.length === 0 && (
          <EmptyState
            size="sm"
            title="Keine Jobs registriert"
            description="Es sind aktuell keine Hintergrund-Jobs im Backend registriert."
          />
        )}

        {jobs.length > 0 && (
          <DataTable<JobStatus>
            data={jobs}
            getRowId={(job) => job.key}
            enableSorting={false}
            columns={[
              {
                accessorKey: 'enabled',
                header: 'Aktiv',
                size: 64,
                cell: ({ row }) => (
                  <Switch
                    checked={row.original.enabled}
                    disabled={toggleMut.isPending}
                    onChange={(e) => toggleMut.mutate({ key: row.original.key, enabled: e.target.checked })}
                  />
                ),
              },
              {
                accessorKey: 'label',
                header: 'Job',
                cell: ({ row }) => (
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {row.original.tags?.length > 0 && row.original.tags.map((tag) => (
                        <Badge key={tag} size="sm" variant={tagVariant(tag)}>
                          {tag}
                        </Badge>
                      ))}
                      <span className="text-body text-gray-900">{row.original.label}</span>
                      {row.original.description && (
                        <Tooltip content={row.original.description} side="right" delayDuration={200}>
                          <span className="inline-flex text-gray-400 hover:text-gray-600 cursor-default">
                            <Info className="w-3.5 h-3.5" />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                    <div className="text-caption text-gray-500 font-mono">{row.original.key}</div>
                    {!row.original.registered && (
                      <div className="text-caption text-amber-700 flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3" />
                        Im Backend nicht mehr registriert
                      </div>
                    )}
                  </div>
                ),
              },
              {
                accessorKey: 'intervalMinutes',
                header: 'Intervall (Min)',
                cell: ({ row }) => {
                  const job = row.original
                  const pendingInterval = intervalEdits[job.key] ?? job.intervalMinutes
                  const intervalDirty = intervalEdits[job.key] !== undefined && intervalEdits[job.key] !== job.intervalMinutes
                  return (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={10080}
                        value={pendingInterval}
                        onChange={(e) =>
                          setIntervalEdits((prev) => ({
                            ...prev,
                            [job.key]: Math.max(1, Number(e.target.value) || 1),
                          }))
                        }
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-body"
                      />
                      {intervalDirty && (
                        <Button
                          variant="primary"
                          size="xs"
                          loading={intervalMut.isPending}
                          onClick={() => intervalMut.mutate({ key: job.key, intervalMinutes: pendingInterval })}
                        >
                          Speichern
                        </Button>
                      )}
                    </div>
                  )
                },
              },
              {
                accessorKey: 'lastRunAt',
                header: 'Letzter Lauf',
                cell: ({ row }) => (
                  <div>
                    <div className="text-body text-gray-700">{formatRelative(row.original.lastRunAt)}</div>
                    {row.original.lastRunDurationMs != null && (
                      <div className="text-caption text-gray-500">
                        Dauer: {formatDuration(row.original.lastRunDurationMs)}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                accessorKey: 'nextDueAt',
                header: 'Nächster Lauf',
                cell: ({ row }) => (
                  <span className="text-body text-gray-700">
                    {row.original.enabled ? formatRelative(row.original.nextDueAt) : '—'}
                  </span>
                ),
              },
              {
                id: 'status',
                header: 'Status',
                cell: ({ row }) => {
                  const job = row.original
                  const hasDetails = !!(job.lastRunMessage || job.lastRunError)
                  const isExpanded = expandedStatus[job.key] ?? false
                  const statusBadge = job.runningSince
                    ? <Badge size="sm" variant="info">läuft …</Badge>
                    : job.lastRunStatus === 'success'
                      ? <Badge size="sm" variant="success">OK</Badge>
                      : job.lastRunStatus === 'error'
                        ? <Badge size="sm" variant="danger">Fehler</Badge>
                        : <Badge size="sm">neu</Badge>
                  return (
                    <div>
                      <button
                        type="button"
                        className={hasDetails ? 'cursor-pointer' : 'cursor-default'}
                        onClick={() => {
                          if (!hasDetails) return
                          setExpandedStatus((prev) => ({ ...prev, [job.key]: !prev[job.key] }))
                        }}
                      >
                        {statusBadge}
                      </button>
                      {isExpanded && (
                        <div className="mt-1 space-y-0.5">
                          {job.lastRunMessage && (
                            <div className="text-caption text-gray-500">{job.lastRunMessage}</div>
                          )}
                          {job.lastRunError && (
                            <div className="text-caption text-red-600">{job.lastRunError}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                },
              },
              {
                id: 'actions',
                header: 'Aktion',
                enableSorting: false,
                cell: ({ row }) => (
                  <div className="flex justify-end gap-2">
                    {row.original.runningSince && (
                      <Button
                        variant="ghost"
                        size="xs"
                        leadingIcon={<Unlock className="w-3 h-3" />}
                        loading={releaseMut.isPending && releaseMut.variables === row.original.key}
                        disabled={releaseMut.isPending || !row.original.registered}
                        onClick={() => setPendingReleaseKey(row.original.key)}
                      >
                        Lock freigeben
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="xs"
                      leadingIcon={<Play className="w-3 h-3" />}
                      loading={runMut.isPending && runMut.variables === row.original.key}
                      disabled={runMut.isPending || !row.original.registered}
                      onClick={() => runMut.mutate(row.original.key)}
                    >
                      Jetzt ausführen
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      <ConfirmDialog
        open={pendingReleaseKey !== null}
        title="Lock freigeben?"
        message={
          pendingReleaseKey
            ? `Der Job "${pendingReleaseKey}" wird auf "läuft" stehen gelassen. Auf Plesk/Passenger lässt sich ein laufender Code nicht hart stoppen — wir geben nur den Lock frei, sodass der nächste Tick wieder starten darf. Falls der Job tatsächlich noch läuft, könnten kurzzeitig zwei parallele Instanzen aktiv sein (für die Bridge-Jobs ist das idempotent und sicher).`
            : ''
        }
        confirmLabel="Lock freigeben"
        variant="danger"
        loading={releaseMut.isPending}
        onConfirm={() => { if (pendingReleaseKey) releaseMut.mutate(pendingReleaseKey) }}
        onCancel={() => setPendingReleaseKey(null)}
      />
    </div>
  )
}

function errMsg(e: unknown): string {
  if (isAxiosError(e)) {
    const d = e.response?.data as { error?: string; message?: string } | undefined
    return d?.error ?? d?.message ?? e.message
  }
  if (e instanceof Error) return e.message
  return String(e)
}
